import { ReactNode, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Activity,
  FileCheck2,
  LayoutDashboard,
  LogOut,
  Menu,
  MessageSquareText,
  Users,
  X,
} from 'lucide-react';
import { useAuthStore } from '../stores/authStore';

interface AppShellProps {
  children: ReactNode;
}

interface NavigationItem {
  label: string;
  path: string;
  icon: typeof LayoutDashboard;
  badge?: string;
}

const primaryNavigation: NavigationItem[] = [
  { label: 'Digest', path: '/dashboard', icon: LayoutDashboard },
  { label: 'Analyzer', path: '/query', icon: MessageSquareText },
];

export function AppShell({ children }: AppShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isAdmin = user?.role === 'admin' || user?.role === 'super_user';

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return (
        location.pathname === '/dashboard' ||
        location.pathname.startsWith('/dashboard/') ||
        location.pathname.startsWith('/signals')
      );
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const goTo = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    setMobileMenuOpen(false);
  };

  const navigation = (
    <>
      <Link
        to="/dashboard"
        className="flex items-center gap-3 px-5 py-5 transition hover:opacity-90 focus:outline-none"
        aria-label="Go to home"
      >
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-700 text-white shadow-[0_0_16px_rgba(37,99,235,0.3)]">
          <Activity className="h-5 w-5" />
        </div>
        <div>
          <div className="text-sm font-black tracking-[0.16em] text-slate-900">SPACELABS</div>
          <div className="text-[10px] font-medium text-slate-500">Healthcare intelligence</div>
        </div>
      </Link>

      <nav aria-label="Primary navigation" className="px-3 pt-4">
        <p className="px-3 pb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">
          Workspace
        </p>
        <div className="space-y-1">
          {primaryNavigation.map(({ label, path, icon: Icon, badge }) => (
            <button
              key={path}
              type="button"
              onClick={() => goTo(path)}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-xs font-semibold transition focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isActive(path)
                  ? 'bg-blue-700 text-white shadow-sm'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950'
              }`}
              aria-current={isActive(path) ? 'page' : undefined}
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="flex-1">{label}</span>
              {badge && (
                <span
                  className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                    isActive(path)
                      ? 'bg-white/20 text-white border-white/25'
                      : 'bg-slate-100 text-slate-500 border-slate-200'
                  }`}
                >
                  {badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </nav>

      {isAdmin && (
        <nav aria-label="Administration navigation" className="px-3 pt-8">
          <p className="px-3 pb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">
            Administration
          </p>
          <div className="space-y-1">
            <button
              type="button"
              onClick={() => goTo('/admin/users')}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-xs font-semibold transition focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isActive('/admin/users')
                  ? 'bg-blue-700 text-white shadow-sm'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950'
              }`}
              aria-current={isActive('/admin/users') ? 'page' : undefined}
            >
              <Users className="h-4 w-4 shrink-0" />
              Team
            </button>
            <button
              type="button"
              onClick={() => goTo('/admin/audit-logs')}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-xs font-semibold transition focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isActive('/admin/audit-logs')
                  ? 'bg-blue-700 text-white shadow-sm'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950'
              }`}
              aria-current={isActive('/admin/audit-logs') ? 'page' : undefined}
            >
              <FileCheck2 className="h-4 w-4 shrink-0" />
              Audit Logs
            </button>
          </div>
        </nav>
      )}

      <div className="mt-auto border-t border-slate-200 p-4">
        {user && (
          <div className="mb-3 min-w-0">
            <div className="truncate text-xs font-semibold text-slate-800">{user.email}</div>
            <div className="mt-0.5 text-[10px] capitalize text-blue-400">{user.role}</div>
          </div>
        )}
        <button
          type="button"
          onClick={handleLogout}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-100 hover:text-slate-950 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#f8fafc] text-slate-900">
      <aside className="no-print fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-slate-200 bg-white shadow-sm lg:flex">
        {navigation}
      </aside>

      <div className="lg:pl-64 print:pl-0 pb-20 lg:pb-0">
        <header className="no-print sticky top-0 z-30 flex h-14 items-center justify-between border-b border-slate-200 bg-white/95 px-4 shadow-xs backdrop-blur lg:hidden">
          <Link
            to="/dashboard"
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center gap-2.5 transition hover:opacity-90 focus:outline-none"
            aria-label="Go to home"
          >
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-blue-700 text-white shadow-xs">
              <Activity className="h-4 w-4" />
            </div>
            <div>
              <span className="text-xs font-black tracking-[0.16em] text-slate-900 uppercase">
                SPACELABS
              </span>
              <span className="hidden sm:inline-block ml-2 text-[10px] text-slate-500 font-medium">
                Healthcare intelligence
              </span>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            {user && (
              <span className="text-[10px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded capitalize">
                {user.role}
              </span>
            )}
            <button
              type="button"
              onClick={() => setMobileMenuOpen((open) => !open)}
              className="rounded-lg p-2 text-slate-600 transition hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-600"
              aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </header>

        {mobileMenuOpen && (
          <>
            <button
              type="button"
              aria-label="Close navigation menu"
              onClick={() => setMobileMenuOpen(false)}
              className="no-print fixed inset-0 z-40 bg-slate-950/40 lg:hidden"
            />
            <aside className="no-print fixed inset-y-0 left-0 z-50 flex w-72 flex-col border-r border-slate-200 bg-white shadow-2xl lg:hidden">
              {navigation}
            </aside>
          </>
        )}

        {children}
      </div>

      {/* Mobile Bottom Tab Navigation */}
      <nav
        aria-label="Mobile bottom navigation"
        className="no-print fixed bottom-0 left-0 right-0 z-40 flex h-16 items-center justify-around border-t border-slate-200 bg-white/95 px-2 backdrop-blur shadow-lg pb-[env(safe-area-inset-bottom)] lg:hidden"
      >
        {primaryNavigation.map(({ label, path, icon: Icon }) => {
          const active = isActive(path);
          return (
            <button
              key={`bottom-${path}`}
              type="button"
              onClick={() => goTo(path)}
              className={`flex flex-col items-center justify-center flex-1 py-1.5 transition ${
                active ? 'text-blue-700 font-semibold' : 'text-slate-500 hover:text-slate-900'
              }`}
              aria-label={`Mobile ${label}`}
              aria-current={active ? 'page' : undefined}
            >
              <Icon className="h-5 w-5 shrink-0" />
              <span className="text-[10px] tracking-tight mt-1">{label}</span>
              {active && <span className="w-1 h-1 rounded-full bg-blue-700 mt-0.5" />}
            </button>
          );
        })}

        {/* More / Menu Tab */}
        <button
          type="button"
          onClick={() => setMobileMenuOpen(true)}
          className={`flex flex-col items-center justify-center flex-1 py-1.5 transition ${
            mobileMenuOpen || (isAdmin && location.pathname.startsWith('/admin'))
              ? 'text-blue-700 font-semibold'
              : 'text-slate-500 hover:text-slate-900'
          }`}
          aria-label={isAdmin ? 'Mobile Admin' : 'Mobile More'}
        >
          <Menu className="h-5 w-5 shrink-0" />
          <span className="text-[10px] tracking-tight mt-1">{isAdmin ? 'Admin' : 'More'}</span>
          {isAdmin && location.pathname.startsWith('/admin') && (
            <span className="w-1 h-1 rounded-full bg-blue-700 mt-0.5" />
          )}
        </button>
      </nav>
    </div>
  );
}
